import { format } from 'date-fns';
import Cookies from 'js-cookie';
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Eye,
  Loader2,
  Mail,
  PenLine,
  Play,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useStartLOAChaserMutation } from '@/services/api/agentflowService';
import { useGetCoverageQuery } from '@/services/api/coverageService';
import { useGetMatterDocumentsQuery } from '@/services/api/mattersService';
import { useListFrontChannels } from '@/services/frontService';
import type { TemplateCategory } from '@/services/types/letters-types';

import { LOAChaserEmailConfig } from './LOAChaserEmailConfig';
import {
  type Activity,
  type Campaign,
  LOAChaserProgress,
  type SLAStep,
} from './LOAChaserProgress';
import { TaskActivityFeed } from './TaskActivityFeed';

// Helper to extract day number from step id (e.g., "day_3_dialpad_call" -> 3)
const extractDayFromStepId = (stepId: string): number => {
  const match = stepId.match(/day_(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

// Calculate next task due date based on flow and starting step
const calculateNextTaskDueDate = (
  steps: readonly { id: string; label: string }[],
  startingStepIndex: number,
): { date: Date; reason: string } => {
  const now = new Date();

  // Look for the next Dialpad call step after starting step
  for (let i = startingStepIndex; i < steps.length; i += 1) {
    const step = steps[i];
    if (step.id.includes('dialpad')) {
      const days = extractDayFromStepId(step.id);
      const date = new Date(now);
      date.setDate(date.getDate() + days);
      return { date, reason: step.label };
    }
  }

  // No Dialpad call found - use the last step's date
  if (steps.length > 0) {
    const lastStep = steps[steps.length - 1];
    const days = extractDayFromStepId(lastStep.id);
    const date = new Date(now);
    date.setDate(date.getDate() + days);
    return { date, reason: 'End of workflow' };
  }

  // Fallback
  const fallbackDate = new Date(now);
  fallbackDate.setDate(fallbackDate.getDate() + 7);
  return { date: fallbackDate, reason: 'Default (7 days)' };
};

// LOA Chaser flow/strategy options (for AgentFlow mode)
const LOA_CHASER_FLOWS = [
  {
    id: 'non_disclosure_3p',
    label: 'Non-Disclosure 3P (Default)',
    description: 'Voice AI call → RingCentral call → Email follow-ups',
    steps: [
      { id: 'day_2_voice_ai', label: 'Day 2: Voice AI Call' },
      { id: 'day_3_dialpad_call', label: 'Day 3: RingCentral Call (LOA + limits)' },
      { id: 'day_5_email', label: 'Day 5: Email follow-up' },
      { id: 'day_7_email', label: 'Day 7: Email follow-up' },
      { id: 'day_9_email', label: 'Day 9: Email follow-up' },
    ],
  },
  {
    id: 'progressive_1p',
    label: 'Progressive 1P',
    description: 'Email follow-ups → RingCentral call → More emails',
    steps: [
      { id: 'day_2_email', label: 'Day 2: Email follow-up' },
      { id: 'day_5_email', label: 'Day 5: Email follow-up' },
      { id: 'day_6_dialpad_call', label: 'Day 6: RingCentral Call (LOA)' },
      { id: 'day_8_email', label: 'Day 8: Email follow-up' },
    ],
  },
  {
    id: 'disclosure',
    label: 'Disclosure',
    description: 'Voice AI → RingCentral → Emails + Text claimant for Dec page',
    steps: [
      { id: 'day_2_voice_ai', label: 'Day 2: Voice AI Call' },
      { id: 'day_3_dialpad_call', label: 'Day 3: RingCentral Call (LOA + Dec page)' },
      { id: 'day_3_email', label: 'Day 3: Email follow-up' },
      { id: 'day_3_text_claimant', label: 'Day 3: Text claimant for Dec page' },
      { id: 'day_6_disclosure_email', label: 'Day 6: Email follow-up' },
      { id: 'day_8_disclosure_email', label: 'Day 8: Email follow-up' },
      { id: 'day_10_disclosure_email', label: 'Day 10: Email follow-up' },
    ],
  },
] as const;

interface LOADetection {
  front_message_id: string;
  is_loa: boolean;
  confidence: number;
  match_signals: Record<string, string | boolean | string[]>;
  message_preview: {
    subject?: string;
    sender?: string;
    created_at?: string;
  };
}

// ============================================================================
// Types
// ============================================================================

interface SearchResult {
  id: string;
  subject?: string | null;
  status?: string | null;
  created_at?: string | null;
  message_count?: number;
  first_message?: {
    id: string;
    subject?: string | null;
    created_at?: string | null;
    is_inbound?: boolean;
    author?: {
      email?: string | null;
      first_name?: string | null;
      last_name?: string | null;
    } | null;
  } | null;
  assignee?: {
    id: string;
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
  recipient?: {
    name?: string | null;
    handle?: string | null;
  } | null;
}

interface ConversationMessage {
  id: string;
  created_at?: string | null;
  subject?: string | null;
  body?: string | null;
  is_inbound?: boolean;
  author?: {
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
  recipients?: Array<{
    name?: string | null;
    handle?: string | null;
    role?: string | null;
  }>;
}

interface CampaignDetail {
  campaign: Campaign;
  workflow_state: {
    status: string;
    current_step_index: number;
    current_step_label: string;
    next_action_date: string | null;
    next_action_type: string | null;
    last_contact_date: string | null;
    total_contacts: number;
  } | null;
}

export interface LOAChaserWidgetProps {
  matterId: string;
  coverageId: string;
  carrierName?: string;
  claimNumber?: string;
  /** Pre-loaded campaign data if available */
  existingCampaign?: CampaignDetail | null;
  /** Pre-loaded SLA steps */
  slaSteps?: SLAStep[];
  /** Callback when campaign is launched */
  onCampaignLaunched?: (campaign: Campaign) => void;
  /** Callback when campaign status changes */
  onCampaignUpdated?: (campaign: CampaignDetail) => void;
  /** Use sandbox endpoint (for testing in Zaddy) - defaults to false for production */
  useSandbox?: boolean;
  /** Suggested LOR email thread DB ID (from deliverable match) to auto-select */
  suggestedLorThreadId?: string;
  /** Task ID for agentflow LOA chaser */
  taskId?: string;
  /** Launch mode: loa_outreach (default) or agentflow */
  launchMode?: 'loa_outreach' | 'agentflow';
  /** Control the launcher modal from a parent */
  launcherOpen?: boolean;
  onLauncherOpenChange?: (open: boolean) => void;
  /** Hide the inline launcher button */
  hideLauncherButton?: boolean;
  /** Default phone number for Voice AI call (from carrier) */
  defaultPhoneNumber?: string | null;
  /** Default adjuster phone number for Dialpad calls (from adjuster) */
  defaultAdjusterPhone?: string | null;
  /** Default recipient email for new email thread (from adjuster) */
  defaultRecipientEmail?: string | null;
}

// API base paths
const getApiBasePath = (useSandbox: boolean) =>
  useSandbox ? '/api/zaddy/loa-outreach' : '/api/loa-outreach';

// URL helpers - sandbox and production have different URL structures
const getCampaignDetailUrl = (
  basePath: string,
  campaignId: string,
  useSandbox: boolean,
) =>
  useSandbox
    ? `${basePath}/campaigns/${campaignId}/`
    : `${basePath}/${campaignId}/`;

const getCampaignSignalUrl = (
  basePath: string,
  campaignId: string,
  useSandbox: boolean,
) =>
  useSandbox
    ? `${basePath}/campaigns/${campaignId}/signal/`
    : `${basePath}/${campaignId}/signal/`;

const getCampaignActivitiesUrl = (
  basePath: string,
  campaignId: string,
  useSandbox: boolean,
) =>
  useSandbox
    ? `${basePath}/campaigns/${campaignId}/activities/`
    : `${basePath}/${campaignId}/activities/`;

// ============================================================================
// LOA Chaser Launcher (No Campaign State)
// ============================================================================

interface LauncherProps {
  matterId: string;
  coverageId: string;
  claimNumber?: string;
  onLaunch: (
    emailThreadId: string,
    phoneNumber?: string,
    adjusterPhone?: string,
    flowId?: string,
    startingStepIndex?: number,
    skipInitialDelay?: boolean,
    emailStepContent?: Record<string, { body: string }>,
  ) => Promise<void>;
  onLaunchWithNewThread: (
    recipientEmail: string,
    subject: string,
    body: string,
    phoneNumber?: string,
    adjusterPhone?: string,
    flowId?: string,
    startingStepIndex?: number,
    skipInitialDelay?: boolean,
    lorDocumentIds?: string[],
    emailStepContent?: Record<string, { body: string }>,
  ) => Promise<void>;
  loading: boolean;
  /** Firm's Front channel address (email) for sending */
  channelAddress?: string | null;
  /** Whether the firm's Front channel is configured */
  hasFrontChannel: boolean;
  /** Whether we're still loading the front channel info */
  isFrontChannelLoading: boolean;
  /** Firm ID for linking to firm settings */
  firmId?: string | null;
  /** Suggested LOR email thread (pre-fetched from LOR deliverable match) */
  suggestedThread?: SearchResult | null;
  /** Whether the suggested thread is being loaded */
  suggestedThreadLoading?: boolean;
  /** Default recipient email for new thread (from carrier/adjuster) */
  defaultRecipientEmail?: string | null;
  /** Default phone number for Voice AI call (from carrier) */
  defaultPhoneNumber?: string | null;
  /** Default adjuster phone number for Dialpad calls (from adjuster) */
  defaultAdjusterPhone?: string | null;
  /** Whether to show phone number input (AgentFlow mode) */
  showPhoneInput?: boolean;
  /** Whether to show chase strategy selector (AgentFlow mode) */
  showStrategySelector?: boolean;
  launcherOpen?: boolean;
  onLauncherOpenChange?: (open: boolean) => void;
  hideLauncherButton?: boolean;
}

type LauncherMode = 'search' | 'create';

const Launcher: React.FC<LauncherProps> = ({
  matterId,
  coverageId,
  claimNumber,
  onLaunch,
  onLaunchWithNewThread,
  loading,
  channelAddress,
  hasFrontChannel,
  isFrontChannelLoading,
  firmId,
  suggestedThread,
  suggestedThreadLoading,
  defaultRecipientEmail,
  defaultPhoneNumber,
  defaultAdjusterPhone,
  showPhoneInput,
  showStrategySelector,
  launcherOpen,
  onLauncherOpenChange,
  hideLauncherButton,
}) => {
  const [mode, setMode] = useState<LauncherMode>('search');
  const [query, setQuery] = useState(claimNumber || '');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedThread, setSelectedThread] = useState<SearchResult | null>(
    null,
  );
  const [hasSearched, setHasSearched] = useState(false);
  const [dismissedSuggestion, setDismissedSuggestion] = useState(false);
  const [internalModalOpen, setInternalModalOpen] = useState(false);

  // Create new thread form state
  const [recipientEmail, setRecipientEmail] = useState(
    defaultRecipientEmail || '',
  );
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [templateLoaded, setTemplateLoaded] = useState(false);

  // LOR document selection state
  const [selectedLorDocs, setSelectedLorDocs] = useState<
    { id: string; name: string }[]
  >([]);
  const [lorQuery, setLorQuery] = useState('');

  // Fetch LOR documents for attachment selection
  const { data: matterDocuments = [] } = useGetMatterDocumentsQuery(matterId, {
    skip: mode !== 'create',
  });

  // Fetch coverage to get previously sent LOR document ID
  const { data: coverage } = useGetCoverageQuery(
    { matterId, coverageId },
    { skip: mode !== 'create' },
  );
  const sentLorDocumentId = coverage?.lor_document_id;

  const lorCategories = React.useMemo(
    () =>
      new Set<TemplateCategory>([
        'LOR_GENERIC',
        'LOR_FIRST_PARTY',
        'LOR_THIRD_PARTY',
        'LOR_HEALTH',
      ]),
    [],
  );
  const availableLorDocs = React.useMemo(
    () =>
      (matterDocuments || [])
        .filter(
          (d) =>
            !!d?.document_template?.category &&
            lorCategories.has(
              d.document_template.category as TemplateCategory,
            ),
        )
        .map((d) => ({
          id: String(d.id),
          name:
            (d.document_template?.name as string | undefined) ||
            'Letter of Representation',
          created_at: d.created_at,
        }))
        .sort((a, b) => {
          // Previously sent LOR document should appear first
          if (sentLorDocumentId) {
            if (a.id === sentLorDocumentId) return -1;
            if (b.id === sentLorDocumentId) return 1;
          }
          // Then sort by date (newest first)
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        }),
    [matterDocuments, lorCategories, sentLorDocumentId],
  );
  const filteredLorDocs = React.useMemo(
    () =>
      availableLorDocs.filter((d) =>
        (d.name || '').toLowerCase().includes(lorQuery.toLowerCase()),
      ),
    [availableLorDocs, lorQuery],
  );

  // Phone number state (for AgentFlow mode)
  const [phoneNumber, setPhoneNumber] = useState(defaultPhoneNumber || '');

  // Adjuster phone number state (for Dialpad calls)
  const [adjusterPhone, setAdjusterPhone] = useState(defaultAdjusterPhone || '');

  // Chase strategy state (for AgentFlow mode)
  const [flowId, setFlowId] = useState<string>('non_disclosure_3p');
  const [startingStepIndex, setStartingStepIndex] = useState<number>(0);
  const [skipInitialDelay, setSkipInitialDelay] = useState<boolean>(false);

  // Email step content state (for AgentFlow mode - configurable email bodies)
  const [emailStepContent, setEmailStepContent] = useState<Record<string, { body: string }>>({});

  // Reset starting step when flow changes
  const handleFlowChange = (newFlowId: string) => {
    setFlowId(newFlowId);
    setStartingStepIndex(0);
  };

  // Get the current flow's steps
  const currentFlow = LOA_CHASER_FLOWS.find((f) => f.id === flowId);
  const currentSteps = currentFlow?.steps ?? [];

  // Calculate next task due date based on selected flow and starting step
  const nextTaskDueDate = React.useMemo(
    () => calculateNextTaskDueDate(currentSteps, startingStepIndex),
    [currentSteps, startingStepIndex],
  );

  // LOA detection state
  const [isDetecting, setIsDetecting] = useState(false);
  const [loaDetections, setLoaDetections] = useState<Map<string, LOADetection>>(
    new Map(),
  );

  // Thread preview state
  const [previewThreadId, setPreviewThreadId] = useState<string | null>(null);
  const [previewMessages, setPreviewMessages] = useState<ConversationMessage[]>(
    [],
  );
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [expandedMessageId, setExpandedMessageId] = useState<string | null>(
    null,
  );

  // Controlled vs uncontrolled modal state
  const isCustomModalOpen = launcherOpen ?? internalModalOpen;
  const setCustomModalOpen = useCallback(
    (open: boolean) => {
      if (onLauncherOpenChange) {
        onLauncherOpenChange(open);
      } else {
        setInternalModalOpen(open);
      }
    },
    [onLauncherOpenChange],
  );

  // Fetch email template when switching to create mode
  useEffect(() => {
    if (mode === 'create' && !templateLoaded && !isLoadingTemplate) {
      const fetchTemplate = async () => {
        setIsLoadingTemplate(true);
        try {
          const url = new URL(
            `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/loa-outreach/email-template/`,
          );
          url.searchParams.set('matter_id', matterId);
          url.searchParams.set('coverage_id', coverageId);
          url.searchParams.set('step_id', 'day_3_email');

          const res = await fetch(url.toString(), { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            setEmailSubject(data.subject || '');
            setEmailBody(data.body || '');
            setTemplateLoaded(true);
          }
        } catch {
          // Silently fail - user can still type manually
        } finally {
          setIsLoadingTemplate(false);
        }
      };
      fetchTemplate();
    }
  }, [mode, matterId, coverageId, templateLoaded, isLoadingTemplate]);

  // Update recipient email when defaultRecipientEmail changes
  useEffect(() => {
    if (defaultRecipientEmail && !recipientEmail) {
      setRecipientEmail(defaultRecipientEmail);
    }
  }, [defaultRecipientEmail, recipientEmail]);

  // Update phone number when defaultPhoneNumber changes
  useEffect(() => {
    if (defaultPhoneNumber && !phoneNumber) {
      setPhoneNumber(defaultPhoneNumber);
    }
  }, [defaultPhoneNumber, phoneNumber]);

  // Update adjuster phone when defaultAdjusterPhone changes
  useEffect(() => {
    if (defaultAdjusterPhone && !adjusterPhone) {
      setAdjusterPhone(defaultAdjusterPhone);
    }
  }, [defaultAdjusterPhone, adjusterPhone]);

  // Auto-select suggested thread if available
  useEffect(() => {
    if (suggestedThread && !selectedThread && !dismissedSuggestion) {
      setSelectedThread(suggestedThread);
    }
  }, [suggestedThread, selectedThread, dismissedSuggestion]);

  // Auto-search on mount if we have a claim number (but not if we have a suggested thread)
  useEffect(() => {
    if (
      claimNumber &&
      !hasSearched &&
      !suggestedThread &&
      !suggestedThreadLoading
    ) {
      handleSearch(claimNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimNumber, suggestedThread, suggestedThreadLoading]);

  const handleSearch = async (q?: string) => {
    const searchQuery = q || query;
    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    try {
      setIsSearching(true);
      setHasSearched(true);
      const url = new URL(
        `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/front/search-messages/`,
      );
      url.searchParams.set('q', searchQuery.trim());
      url.searchParams.set('coverage_id', coverageId);

      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error(`Search failed (${res.status})`);

      const data = (await res.json()) as
        | { results?: SearchResult[] }
        | undefined;
      const list = Array.isArray(data?.results) ? data.results : [];

      // Filter out Filevine reminder noise
      const filtered = list.filter((r) => {
        const subject = (r.subject || '').toLowerCase();
        if (subject.startsWith('[filevine]')) return false;
        const authorEmail = r.first_message?.author?.email?.toLowerCase();
        return authorEmail !== 'reminders@filevine.com';
      });

      setResults(filtered);
      // Clear old detections when new search is performed
      setLoaDetections(new Map());
    } catch {
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const handlePreviewThread = async (threadId: string) => {
    // Toggle off if already previewing this thread
    if (previewThreadId === threadId) {
      setPreviewThreadId(null);
      setPreviewMessages([]);
      setExpandedMessageId(null);
      return;
    }

    setIsLoadingPreview(true);
    setPreviewThreadId(threadId);
    setPreviewMessages([]);
    setExpandedMessageId(null);

    try {
      const res = await fetch(
        `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/front/conversations/${threadId}/`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(`Failed to fetch messages (${res.status})`);

      const data = await res.json();
      setPreviewMessages(data.messages || []);
    } catch {
      toast.error('Failed to load email preview');
      setPreviewThreadId(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleFindLOA = async () => {
    if (results.length === 0) {
      toast.error('No search results to analyze');
      return;
    }

    setIsDetecting(true);
    try {
      // Get all first message IDs from search results
      const messageIds = results
        .map((r) => r.first_message?.id)
        .filter((id): id is string => !!id);

      if (messageIds.length === 0) {
        toast.error('No messages to analyze');
        return;
      }

      // Use the first result's conversation ID
      const conversationId = results[0]?.id;

      const res = await fetch(
        `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/front/${coverageId}/detect-loa/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': Cookies.get('csrftoken') || '',
          },
          credentials: 'include',
          body: JSON.stringify({
            front_message_ids: messageIds,
            conversation_id: conversationId,
          }),
        },
      );

      if (!res.ok) throw new Error(`Detection failed (${res.status})`);

      const data = (await res.json()) as { detections?: LOADetection[] };
      const detections = data?.detections || [];

      // Build a map from conversation ID to detection result
      const detectionMap = new Map<string, LOADetection>();
      detections.forEach((d) => {
        // Find the result that matches this message
        const matchingResult = results.find(
          (r) => r.first_message?.id === d.front_message_id,
        );
        if (matchingResult && d.is_loa) {
          detectionMap.set(matchingResult.id, d);
        }
      });

      setLoaDetections(detectionMap);

      const loaCount = detections.filter((d) => d.is_loa).length;
      if (loaCount > 0) {
        toast.success(
          `Found ${loaCount} potential LOA${loaCount > 1 ? 's' : ''}`,
        );
        // Auto-select the first detected LOA
        const firstLOA = detections.find((d) => d.is_loa);
        if (firstLOA) {
          const matchingResult = results.find(
            (r) => r.first_message?.id === firstLOA.front_message_id,
          );
          if (matchingResult) {
            setSelectedThread(matchingResult);
          }
        }
      } else {
        toast.info('No LOA detected in search results');
      }
    } catch {
      toast.error('LOA detection failed');
    } finally {
      setIsDetecting(false);
    }
  };

  const handleLaunch = async () => {
    if (!selectedThread) {
      toast.error('Please select an email thread');
      return;
    }
    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number for Voice AI call');
      return;
    }
    await onLaunch(
      selectedThread.id,
      phoneNumber.trim() || undefined,
      adjusterPhone.trim() || undefined,
      showStrategySelector ? flowId : undefined,
      showStrategySelector ? startingStepIndex : undefined,
      showStrategySelector ? skipInitialDelay : undefined,
      showStrategySelector && Object.keys(emailStepContent).length > 0
        ? emailStepContent
        : undefined,
    );
  };

  const handleLaunchWithNewThread = async () => {
    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number for Voice AI call');
      return;
    }
    if (!recipientEmail.trim()) {
      toast.error('Please enter a recipient email');
      return;
    }
    if (!emailSubject.trim()) {
      toast.error('Please enter an email subject');
      return;
    }
    if (!emailBody.trim()) {
      toast.error('Please enter an email body');
      return;
    }
    const lorDocIds =
      selectedLorDocs.length > 0
        ? selectedLorDocs.map((d) => d.id)
        : undefined;
    await onLaunchWithNewThread(
      recipientEmail.trim(),
      emailSubject,
      emailBody,
      phoneNumber.trim() || undefined,
      adjusterPhone.trim() || undefined,
      showStrategySelector ? flowId : undefined,
      showStrategySelector ? startingStepIndex : undefined,
      showStrategySelector ? skipInitialDelay : undefined,
      lorDocIds,
      showStrategySelector && Object.keys(emailStepContent).length > 0
        ? emailStepContent
        : undefined,
    );
  };

  // When hideLauncherButton is true, only render the Dialog (no inline card)
  // This is used when the modal is controlled externally (e.g., Custom LOA Chase button)
  const dialogElement = (
    <Dialog open={isCustomModalOpen} onOpenChange={setCustomModalOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>LOA Chase</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto pr-1 flex-1">
            {/* Content Section */}
            <div className="space-y-3">
              {/* Front channel missing warning */}
              {!isFrontChannelLoading && !hasFrontChannel && (
                <div className="rounded border border-amber-200 bg-amber-50 p-2.5 flex flex-between w-full">
                  <div className="flex flex-col">
                    <div className="text-xs font-semibold text-amber-800">
                      Front channel not configured
                    </div>
                    <div className="text-[11px] text-amber-700 mt-1">
                      Set your firm&apos;s Front channel in Firm settings to
                      enable automated LOA follow-ups.
                    </div>
                  </div>
                </div>
              )}

              <div className="text-sm font-medium text-zinc-900">
                Start LOA Follow-up Campaign
              </div>

              {/* Mode Toggle */}
              <div className="flex items-center gap-1 p-0.5 bg-zinc-100 rounded-md w-fit">
                <button
                  type="button"
                  onClick={() => setMode('search')}
                  className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition-colors',
                    mode === 'search'
                      ? 'bg-white text-zinc-900 shadow-sm'
                      : 'text-zinc-600 hover:text-zinc-900',
                  )}
                >
                  <Search className="h-3 w-3" />
                  Find Email Thread
                </button>
                <button
                  type="button"
                  onClick={() => setMode('create')}
                  className={cn(
                    'flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition-colors',
                    mode === 'create'
                      ? 'bg-white text-zinc-900 shadow-sm'
                      : 'text-zinc-600 hover:text-zinc-900',
                  )}
                >
                  <PenLine className="h-3 w-3" />
                  Create New Thread
                </button>
              </div>

              {/* Chase Strategy Selector (AgentFlow mode) */}
              {showStrategySelector && (
                <div className="space-y-3">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="block">
                    <span className="block text-xs font-medium text-zinc-700 mb-1">
                      Chase Strategy
                    </span>
                    <Select value={flowId} onValueChange={handleFlowChange}>
                      <SelectTrigger className="w-full h-9">
                        <SelectValue placeholder="Select a strategy" />
                      </SelectTrigger>
                      <SelectContent>
                        {LOA_CHASER_FLOWS.map((flow) => (
                          <SelectItem key={flow.id} value={flow.id}>
                            {flow.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-[10px] text-zinc-400">
                      {currentFlow?.description}
                    </p>
                  </label>

                  {/* Starting Step Selector */}
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="block">
                    <span className="block text-xs font-medium text-zinc-700 mb-1">
                      Start at Step
                    </span>
                    <Select
                      value={String(startingStepIndex)}
                      onValueChange={(v) => setStartingStepIndex(Number(v))}
                    >
                      <SelectTrigger className="w-full h-9">
                        <SelectValue placeholder="Select starting step" />
                      </SelectTrigger>
                      <SelectContent>
                        {currentSteps.map((step, index) => (
                          <SelectItem key={step.id} value={String(index)}>
                            {step.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-[10px] text-zinc-400">
                      Steps before this will be skipped
                    </p>
                  </label>

                  {/* Skip Initial Delay Checkbox */}
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={skipInitialDelay}
                      onChange={(e) => setSkipInitialDelay(e.target.checked)}
                      className="h-4 w-4 rounded border-zinc-300 text-violet-600 focus:ring-violet-500"
                    />
                    <div>
                      <span className="text-xs font-medium text-zinc-700">
                        Execute immediately
                      </span>
                      <p className="text-[10px] text-zinc-400">
                        Skip the wait/delay for the first step
                      </p>
                    </div>
                  </label>

                  {/* Task Due Date Preview */}
                  <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="text-[11px] text-amber-800">
                      <span className="font-medium">Task due date will be updated to:</span>
                      <div className="mt-1 font-semibold">
                        {format(nextTaskDueDate.date, 'MMM d, yyyy')}
                      </div>
                      <div className="text-[10px] text-amber-600 mt-0.5">
                        {nextTaskDueDate.reason}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Phone Number Input (AgentFlow mode) */}
              {showPhoneInput && (
                /* eslint-disable-next-line jsx-a11y/label-has-associated-control */
                <label className="block">
                  <span className="block text-xs font-medium text-zinc-700 mb-1">
                    Phone Number for Voice AI Call{' '}
                    <span className="font-normal text-zinc-500">
                      (optional)
                    </span>
                  </span>
                  <Input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="h-8 text-sm"
                  />
                  <p className="mt-1 text-[10px] text-zinc-400">
                    Carrier phone for automated Voice AI call
                  </p>
                </label>
              )}

              {/* Adjuster Phone Number Input (AgentFlow mode - for Dialpad) */}
              {showPhoneInput && (
                /* eslint-disable-next-line jsx-a11y/label-has-associated-control */
                <label className="block">
                  <span className="block text-xs font-medium text-zinc-700 mb-1">
                    Adjuster Phone for RingCentral Call{' '}
                    <span className="font-normal text-zinc-500">
                      (optional)
                    </span>
                  </span>
                  <Input
                    type="tel"
                    value={adjusterPhone}
                    onChange={(e) => setAdjusterPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="h-8 text-sm"
                  />
                  <p className="mt-1 text-[10px] text-zinc-400">
                    Adjuster phone for BPO RingCentral calls
                  </p>
                </label>
              )}

        {mode === 'search' ? (
          <>
            <p className="text-xs text-zinc-500">
              Search for the LOR email sent to the carrier and select it to
              begin automated follow-ups.
            </p>

                  {/* Search Input */}
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
                      <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder="Search by claim #, policy #, or subject..."
                        className="pl-8 h-8 text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSearch()}
                      disabled={isSearching || !query.trim()}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-zinc-900 hover:bg-zinc-800 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSearching ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Search className="h-3.5 w-3.5" />
                      )}
                      Search
                    </button>
                    <button
                      type="button"
                      onClick={handleFindLOA}
                      disabled={isDetecting || results.length === 0}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isDetecting ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="h-3.5 w-3.5" />
                      )}
                      Find LOA
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs text-zinc-500">
                    If the LOR was sent by fax, create a new email thread to
                    begin follow-ups.
                  </p>

                  {/* Email Compose Form */}
                  {isLoadingTemplate ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
                      <span className="ml-2 text-xs text-zinc-500">
                        Loading template...
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="block">
                        <span className="block text-xs font-medium text-zinc-700 mb-1">
                          Recipient Email
                        </span>
                        <Input
                          type="email"
                          value={recipientEmail}
                          onChange={(e) => setRecipientEmail(e.target.value)}
                          placeholder="adjuster@carrier.com"
                          className="h-8 text-sm"
                        />
                      </label>
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="block">
                        <span className="block text-xs font-medium text-zinc-700 mb-1">
                          Subject
                        </span>
                        <Input
                          type="text"
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          placeholder="Re: Claim Acknowledgment Request"
                          className="h-8 text-sm"
                        />
                      </label>
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="block">
                        <span className="block text-xs font-medium text-zinc-700 mb-1">
                          Message
                        </span>
                        <Textarea
                          value={emailBody.replace(/<[^>]*>/g, '\n').trim()}
                          onChange={(e) => setEmailBody(e.target.value)}
                          placeholder="Enter your message..."
                          rows={6}
                          className="text-sm resize-none"
                        />
                        <p className="mt-1 text-[10px] text-zinc-400">
                          Template prefilled. Edit as needed.
                        </p>
                      </label>

                      {/* LOR Document Attachment Section */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-zinc-700">
                            Attach LOR Document{' '}
                            <span className="font-normal text-zinc-400">
                              (optional)
                            </span>
                          </span>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="text-[10px] font-semibold text-violet-600 hover:text-violet-700"
                              >
                                + Select LOR
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[280px] p-2" align="end">
                              <input
                                className="w-full rounded-md border border-zinc-200 px-2 py-1.5 text-xs font-medium text-zinc-800"
                                placeholder="Search LOR documents…"
                                value={lorQuery}
                                onChange={(e) => setLorQuery(e.target.value)}
                                aria-label="Search LOR documents"
                              />
                              <div className="mt-1.5 max-h-40 overflow-auto">
                                {filteredLorDocs.length === 0 ? (
                                  <div className="px-2 py-2 text-[10px] text-zinc-400">
                                    No LOR documents found
                                  </div>
                                ) : (
                                  <div className="space-y-0.5">
                                    {filteredLorDocs.map((d) => {
                                      const isAdded = selectedLorDocs.some(
                                        (x) => x.id === d.id,
                                      );
                                      const isSentLor =
                                        sentLorDocumentId === d.id;
                                      return (
                                        <button
                                          key={d.id}
                                          type="button"
                                          onClick={() =>
                                            setSelectedLorDocs((prev) =>
                                              isAdded
                                                ? prev.filter((x) => x.id !== d.id)
                                                : [...prev, { id: d.id, name: d.name }],
                                            )
                                          }
                                          className={cn(
                                            'w-full px-2 py-1.5 text-left text-xs font-medium rounded-md hover:bg-zinc-50',
                                            isAdded
                                              ? 'bg-violet-50 text-violet-800'
                                              : isSentLor
                                                ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
                                                : 'text-zinc-700',
                                          )}
                                          aria-label={`${isAdded ? 'Remove' : 'Attach'} ${d.name}${isSentLor ? ' (Previously Sent)' : ''}`}
                                        >
                                          <div className="flex items-center justify-between gap-1">
                                            <span className="truncate">
                                              {d.name}
                                            </span>
                                            {isSentLor && (
                                              <Badge
                                                variant="outline"
                                                className="shrink-0 border-emerald-300 bg-emerald-100 text-[9px] px-1 py-0 text-emerald-700"
                                              >
                                                Sent
                                              </Badge>
                                            )}
                                          </div>
                                          <span className="text-[10px] text-zinc-400">
                                            {d.created_at
                                              ? format(
                                                  new Date(d.created_at),
                                                  'MMM d, yyyy',
                                                )
                                              : ''}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                        {selectedLorDocs.length > 0 && (
                          <div className="space-y-1">
                            {selectedLorDocs.map((d) => (
                              <div
                                key={d.id}
                                className="flex items-center justify-between rounded-md bg-violet-50 px-2 py-1.5 text-xs border border-violet-100"
                              >
                                <span className="truncate font-medium text-violet-800">
                                  {d.name}
                                </span>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setSelectedLorDocs((prev) =>
                                      prev.filter((x) => x.id !== d.id),
                                    )
                                  }
                                  className="text-zinc-400 hover:text-red-500 ml-2"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {channelAddress && (
                        <div className="px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg">
                          <div className="text-[11px] text-zinc-600">
                            <span className="font-medium">
                              This email will be sent from {channelAddress}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Selected Thread Display */}
              {selectedThread && (
                <div className="space-y-2">
                  {/* Show "Suggested" indicator if this is the auto-suggested LOR thread */}
                  {suggestedThread &&
                    selectedThread.id === suggestedThread.id && (
                      <div className="flex items-center gap-1.5 text-[11px] text-emerald-700">
                        <span className="px-1.5 py-0.5 bg-emerald-100 rounded font-medium">
                          Suggested
                        </span>
                        <span>from your sent LOR email</span>
                      </div>
                    )}
                  <div className="flex items-center gap-2 px-3 py-2 bg-violet-50 border border-violet-200 rounded-lg">
                    <Mail className="h-4 w-4 text-violet-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-violet-900 truncate">
                        {selectedThread.subject || 'No subject'}
                      </div>
                      <div className="text-[11px] text-violet-600 space-y-0.5">
                        <div>
                          {selectedThread.created_at
                            ? format(
                                new Date(selectedThread.created_at),
                                'MMM d, yyyy h:mm a',
                              )
                            : ''}
                        </div>
                        <div className="flex flex-wrap gap-x-2">
                          {channelAddress && (
                            <span>From: {channelAddress}</span>
                          )}
                          {selectedThread.recipient?.handle && (
                            <span>To: {selectedThread.recipient.handle}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedThread(null);
                        // Mark suggestion as dismissed so it doesn't auto-select again
                        if (
                          suggestedThread &&
                          selectedThread.id === suggestedThread.id
                        ) {
                          setDismissedSuggestion(true);
                        }
                      }}
                      className="p-1 text-violet-500 hover:text-violet-700 hover:bg-violet-100 rounded transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {/* Info about where follow-ups will be sent from */}
                  {channelAddress && (
                    <div className="px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg">
                      <div className="text-[11px] text-zinc-600">
                        <span className="font-medium">
                          Follow-ups will be sent from {channelAddress}
                        </span>{' '}
                        as replies to this email thread.
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Loading States */}
              {(isSearching || suggestedThreadLoading) && !selectedThread && (
                <div className="flex flex-col items-center justify-center py-6 gap-2">
                  <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
                  <span className="text-xs text-zinc-500">
                    {suggestedThreadLoading
                      ? 'Loading suggested email...'
                      : 'Searching emails...'}
                  </span>
                </div>
              )}

              {/* Search Results */}
              {hasSearched && !selectedThread && !isSearching && (
                <div className="border border-zinc-200 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200">
                    <span className="text-[11px] font-medium text-zinc-600">
                      {results.length} result{results.length !== 1 ? 's' : ''}{' '}
                      found
                    </span>
                  </div>
                  <div className="max-h-64 overflow-auto">
                    {results.length === 0 ? (
                      <div className="px-3 py-4 text-center text-xs text-zinc-500">
                        No emails found. Try a different search term.
                      </div>
                    ) : (
                      <ul className="divide-y divide-zinc-100">
                        {results.map((r) => {
                          const detection = loaDetections.get(r.id);
                          const isLOA = !!detection;
                          const isPreviewing = previewThreadId === r.id;
                          return (
                            <li key={r.id}>
                              <div
                                className={cn(
                                  'flex items-center w-full px-3 py-2 hover:bg-zinc-50 transition-colors',
                                  isLOA && 'bg-emerald-50 hover:bg-emerald-100',
                                  isPreviewing && 'bg-violet-50',
                                )}
                              >
                                <button
                                  type="button"
                                  onClick={() => setSelectedThread(r)}
                                  className="flex items-center gap-2 flex-1 min-w-0 text-left"
                                >
                                  <Mail
                                    className={cn(
                                      'h-3.5 w-3.5 shrink-0',
                                      isLOA
                                        ? 'text-emerald-600'
                                        : 'text-zinc-400',
                                    )}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span
                                        className={cn(
                                          'text-sm font-medium truncate',
                                          isLOA
                                            ? 'text-emerald-900'
                                            : 'text-zinc-900',
                                        )}
                                      >
                                        {r.subject || 'No subject'}
                                      </span>
                                      {isLOA && (
                                        <Badge
                                          variant="secondary"
                                          className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] px-1.5 py-0 shrink-0"
                                        >
                                          LOA Detected
                                        </Badge>
                                      )}
                                    </div>
                                    <div
                                      className={cn(
                                        'flex items-center gap-2 text-[11px]',
                                        isLOA
                                          ? 'text-emerald-600'
                                          : 'text-zinc-500',
                                      )}
                                    >
                                      <span>
                                        {r.created_at
                                          ? format(
                                              new Date(r.created_at),
                                              'MMM d, yyyy',
                                            )
                                          : ''}
                                      </span>
                                      {r.recipient?.handle && (
                                        <>
                                          <span>•</span>
                                          <span className="truncate">
                                            To: {r.recipient.handle}
                                          </span>
                                        </>
                                      )}
                                      {r.message_count &&
                                        r.message_count > 1 && (
                                          <>
                                            <span>•</span>
                                            <span>
                                              {r.message_count} messages
                                            </span>
                                          </>
                                        )}
                                    </div>
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePreviewThread(r.id);
                                  }}
                                  className={cn(
                                    'p-1.5 rounded transition-colors shrink-0',
                                    isPreviewing
                                      ? 'bg-violet-100 text-violet-700'
                                      : isLOA
                                        ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100'
                                        : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100',
                                  )}
                                  title={
                                    isPreviewing
                                      ? 'Hide preview'
                                      : 'Preview messages'
                                  }
                                >
                                  {isLoadingPreview && isPreviewing ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Eye className="h-3.5 w-3.5" />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(
                                      `https://app.frontapp.com/open/${r.id}`,
                                      '_blank',
                                    );
                                  }}
                                  className={cn(
                                    'p-1.5 hover:bg-zinc-100 rounded transition-colors shrink-0',
                                    isLOA
                                      ? 'text-emerald-600 hover:text-emerald-700'
                                      : 'text-zinc-400 hover:text-zinc-600',
                                  )}
                                  title="Open in Front"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </button>
                              </div>
                              {/* Message Preview Panel */}
                              {isPreviewing && previewMessages.length > 0 && (
                                <div className="px-3 pb-3 pt-1 bg-violet-50/50 border-t border-violet-100">
                                  <div className="space-y-2">
                                    {previewMessages.map((msg) => {
                                      const isExpanded =
                                        expandedMessageId === msg.id;
                                      const authorName =
                                        msg.author?.first_name ||
                                        msg.author?.email ||
                                        'Unknown';
                                      const bodyText = msg.body
                                        ?.replace(/<[^>]*>/g, ' ')
                                        .replace(/\s+/g, ' ')
                                        .trim();
                                      const truncatedBody =
                                        bodyText && bodyText.length > 150
                                          ? `${bodyText.slice(0, 150)}...`
                                          : bodyText;

                                      return (
                                        <div
                                          key={msg.id}
                                          className="bg-white rounded border border-violet-200 overflow-hidden"
                                        >
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setExpandedMessageId(
                                                isExpanded ? null : msg.id,
                                              )
                                            }
                                            className="w-full px-2.5 py-1.5 flex items-start gap-2 text-left hover:bg-violet-50 transition-colors"
                                          >
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2 text-[11px]">
                                                <span
                                                  className={cn(
                                                    'font-medium',
                                                    msg.is_inbound
                                                      ? 'text-violet-700'
                                                      : 'text-zinc-700',
                                                  )}
                                                >
                                                  {msg.is_inbound
                                                    ? '← '
                                                    : '→ '}
                                                  {authorName}
                                                </span>
                                                <span className="text-zinc-400">
                                                  {msg.created_at
                                                    ? format(
                                                        new Date(msg.created_at),
                                                        'MMM d, h:mm a',
                                                      )
                                                    : ''}
                                                </span>
                                              </div>
                                              <div className="text-[11px] text-zinc-600 mt-0.5">
                                                {isExpanded
                                                  ? bodyText || 'No content'
                                                  : truncatedBody ||
                                                    'No content'}
                                              </div>
                                            </div>
                                            {bodyText &&
                                              bodyText.length > 150 && (
                                                <div className="shrink-0 mt-0.5">
                                                  {isExpanded ? (
                                                    <ChevronUp className="h-3 w-3 text-zinc-400" />
                                                  ) : (
                                                    <ChevronDown className="h-3 w-3 text-zinc-400" />
                                                  )}
                                                </div>
                                              )}
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {/* Follow-up Email Templates (AgentFlow mode) */}
              {showStrategySelector && (
                <div className="border-t border-zinc-200 pt-3">
                  <LOAChaserEmailConfig
                    flowId={flowId}
                    onChange={setEmailStepContent}
                  />
                </div>
              )}
            </div>

            {/* Launch Button */}
            <div className="flex justify-end px-3 py-2.5 bg-zinc-50 border-t border-zinc-100">
              {mode === 'search' ? (
                <button
                  type="button"
                  onClick={handleLaunch}
                  disabled={loading || !selectedThread || !hasFrontChannel}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-md transition-colors',
                    selectedThread && hasFrontChannel
                      ? 'bg-violet-600 text-white hover:bg-violet-700'
                      : 'bg-zinc-200 text-zinc-500 cursor-not-allowed',
                    loading && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  Enable Auto-Chase
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleLaunchWithNewThread}
                  disabled={
                    loading ||
                    !recipientEmail.trim() ||
                    !emailSubject.trim() ||
                    !emailBody.trim() ||
                    !hasFrontChannel ||
                    isLoadingTemplate
                  }
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-md transition-colors',
                    recipientEmail.trim() &&
                      emailSubject.trim() &&
                      emailBody.trim() &&
                      hasFrontChannel &&
                      !isLoadingTemplate
                      ? 'bg-violet-600 text-white hover:bg-violet-700'
                      : 'bg-zinc-200 text-zinc-500 cursor-not-allowed',
                    loading && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      Send & Enable Auto-Chase
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </DialogContent>
    </Dialog>
  );

  // When hideLauncherButton is true, only render the dialog (controlled externally)
  if (hideLauncherButton) {
    return dialogElement;
  }

  // Otherwise, render the full inline card with the dialog
  return (
    <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-zinc-900">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-white">LOA Chaser</span>
          <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-zinc-700 text-zinc-400">
            Not Started
          </span>
        </div>
      </div>

      <div className="p-3 space-y-3">
        <div className="text-sm font-medium text-zinc-900">LOA Chase</div>
        <p className="text-xs text-zinc-500">
          Configure the claims call and follow-ups before starting.
        </p>
        <button
          type="button"
          onClick={() => setCustomModalOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-md bg-violet-600 text-white hover:bg-violet-700"
        >
          <Play className="h-4 w-4" />
          LOA Chase
        </button>
      </div>

      {dialogElement}
    </div>
  );
};

// ============================================================================
// Main Widget Component
// ============================================================================

export const LOAChaserWidget: React.FC<LOAChaserWidgetProps> = ({
  matterId,
  coverageId,
  carrierName,
  claimNumber,
  existingCampaign: initialCampaign,
  slaSteps: initialSlaSteps,
  onCampaignLaunched,
  onCampaignUpdated,
  useSandbox = false,
  suggestedLorThreadId,
  taskId,
  launchMode = 'loa_outreach',
  launcherOpen,
  onLauncherOpenChange,
  hideLauncherButton,
  defaultPhoneNumber,
  defaultAdjusterPhone,
  defaultRecipientEmail,
}) => {
  const [campaign, setCampaign] = useState<CampaignDetail | null>(
    initialCampaign || null,
  );
  const [slaSteps, setSlaSteps] = useState<SLAStep[]>(initialSlaSteps || []);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(
    !initialCampaign && !initialSlaSteps,
  );
  const [signalLoading, setSignalLoading] = useState(false);

  // AgentFlow mutation for custom LOA chaser
  const [startLOAChaser] = useStartLOAChaserMutation();

  // Suggested LOR thread state
  const [suggestedThread, setSuggestedThread] = useState<SearchResult | null>(
    null,
  );
  const [suggestedThreadLoading, setSuggestedThreadLoading] = useState(false);

  const apiBasePath = getApiBasePath(useSandbox);

  // Fetch firm's Front channel from firm settings (same pattern as SendFrontEmailDialog)
  const { channels } = useListFrontChannels(matterId);
  const [firmChannelId, setFirmChannelId] = useState<string | null>(null);
  const [firmId, setFirmId] = useState<string | null>(null);
  const [isFrontChannelLoading, setIsFrontChannelLoading] = useState(true);

  // Fetch firm channel ID from matter's firm settings
  useEffect(() => {
    const fetchFirmChannel = async () => {
      setIsFrontChannelLoading(true);
      try {
        const res = await fetch(
          `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/`,
          { credentials: 'include' },
        );
        if (res.ok) {
          const data = await res.json();
          const channelId =
            (data?.firm?.front_channel?.front_id as string | undefined) || null;
          setFirmChannelId(channelId);
          setFirmId((data?.firm?.id as string | undefined) || null);
        }
      } catch {
        // Silently fail - channel address is optional
      } finally {
        setIsFrontChannelLoading(false);
      }
    };
    fetchFirmChannel();
  }, [matterId]);

  // Look up the channel address from the firm's configured channel
  const firmChannel = channels?.find((c) => c.id === firmChannelId);
  const channelAddress = firmChannel?.address || null;
  const hasFrontChannel = !!firmChannelId;

  // Fetch suggested LOR thread details if provided
  useEffect(() => {
    if (!suggestedLorThreadId || !matterId) return;

    const fetchSuggestedThread = async () => {
      setSuggestedThreadLoading(true);
      try {
        // Fetch conversation details using the DB ID
        const res = await fetch(
          `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/front/conversation-by-db-id/${suggestedLorThreadId}/`,
          { credentials: 'include' },
        );
        if (res.ok) {
          const data = await res.json();
          const { conversation } = data;
          if (conversation) {
            // Map to SearchResult format
            setSuggestedThread({
              id: conversation.id, // Front's cnv_xxx ID
              subject: conversation.subject,
              status: conversation.status,
              created_at: conversation.created_at,
              recipient: conversation.recipient,
              assignee: conversation.assignee,
            });
          }
        }
      } catch {
        // Silently fail - suggestion is optional
      } finally {
        setSuggestedThreadLoading(false);
      }
    };

    fetchSuggestedThread();
  }, [suggestedLorThreadId, matterId]);

  // Fetch activities for a campaign
  const fetchActivities = useCallback(
    async (campaignId: string) => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${getCampaignActivitiesUrl(apiBasePath, campaignId, useSandbox)}`,
          { credentials: 'include' },
        );
        if (res.ok) {
          const data = await res.json();
          setActivities(data.activities || []);
        }
      } catch {
        // Silently fail - activities are optional
      }
    },
    [apiBasePath, useSandbox],
  );

  // Fetch SLA steps and existing campaign on mount
  useEffect(() => {
    const init = async () => {
      try {
        // Fetch SLA steps if not provided
        if (!initialSlaSteps || initialSlaSteps.length === 0) {
          const stepsRes = await fetch(
            `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${apiBasePath}/sla-steps/`,
            { credentials: 'include' },
          );
          if (stepsRes.ok) {
            const stepsData = await stepsRes.json();
            setSlaSteps(stepsData.steps || []);
          }
        }

        // Fetch existing campaign for this coverage if not provided
        // Skip fetch if initialCampaign is explicitly null (force launcher mode)
        if (initialCampaign === undefined) {
          const campaignRes = await fetch(
            `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${apiBasePath}/campaigns/?coverage_id=${coverageId}`,
            { credentials: 'include' },
          );
          if (campaignRes.ok) {
            const campaignData = await campaignRes.json();
            const existingCampaigns = campaignData.campaigns || [];
            if (existingCampaigns.length > 0) {
              // Fetch full campaign details
              const detailRes = await fetch(
                `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${getCampaignDetailUrl(apiBasePath, existingCampaigns[0].id, useSandbox)}`,
                { credentials: 'include' },
              );
              if (detailRes.ok) {
                const detailData = await detailRes.json();
                setCampaign(detailData);
                // Fetch activities for this campaign
                await fetchActivities(existingCampaigns[0].id);
              }
            }
          }
        } else if (initialCampaign) {
          // Fetch activities for provided campaign
          await fetchActivities(initialCampaign.campaign.id);
        }
      } catch {
        // Initialization failed silently - component will show launcher
      } finally {
        setIsInitializing(false);
      }
    };

    init();
  }, [
    coverageId,
    initialCampaign,
    initialSlaSteps,
    apiBasePath,
    useSandbox,
    fetchActivities,
  ]);

  // Launch campaign with existing email thread
  const handleLaunch = useCallback(
    async (
      emailThreadId: string,
      phoneNumber?: string,
      adjusterPhone?: string,
      flowId?: string,
      startingStepIndex?: number,
      skipInitialDelay?: boolean,
      emailStepContent?: Record<string, { body: string }>,
    ) => {
      setLoading(true);
      try {
        // AgentFlow mode - use custom LOA chaser workflow
        if (launchMode === 'agentflow') {
          if (!taskId) {
            toast.error('No acknowledgement task found for this coverage');
            return;
          }
          // Convert body-only content to API format (subject will be derived from thread)
          const apiEmailContent = emailStepContent
            ? Object.fromEntries(
                Object.entries(emailStepContent).map(([stepId, { body }]) => [
                  stepId,
                  { subject: '', body },
                ]),
              )
            : undefined;
          await startLOAChaser({
            matterId,
            coverageId,
            request: {
              task_id: taskId,
              email_thread_id: emailThreadId,
              phone_number: phoneNumber,
              adjuster_phone: adjusterPhone,
              flow_id: flowId,
              starting_step_index: startingStepIndex,
              skip_initial_delay: skipInitialDelay,
              email_step_content: apiEmailContent,
            },
          }).unwrap();
          toast.success('LOA chaser started');
          return;
        }

        // LOA Outreach mode - use the legacy loa-outreach API
        // Production uses /launch, sandbox uses /launch-test
        const launchUrl = useSandbox
          ? `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${apiBasePath}/launch-test/`
          : `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${apiBasePath}/launch/`;

        const res = await fetch(launchUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': Cookies.get('csrftoken') || '',
          },
          credentials: 'include',
          body: JSON.stringify({
            matter_id: matterId,
            coverage_id: coverageId,
            email_thread_id: emailThreadId,
            ...(useSandbox ? { starting_step_index: 0 } : {}),
          }),
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || `Launch failed (${res.status})`);
        }

        const data = await res.json();
        toast.success('Auto-chase enabled successfully');

        // Fetch the full campaign details
        const detailRes = await fetch(
          `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${getCampaignDetailUrl(apiBasePath, data.campaign_id, useSandbox)}`,
          { credentials: 'include' },
        );
        if (detailRes.ok) {
          const detailData = await detailRes.json();
          setCampaign(detailData);
          onCampaignLaunched?.(detailData.campaign);
          // Fetch activities for the new campaign
          await fetchActivities(data.campaign_id);
        }
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : 'Failed to enable auto-chase';
        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [
      matterId,
      coverageId,
      onCampaignLaunched,
      apiBasePath,
      useSandbox,
      fetchActivities,
      launchMode,
      taskId,
      startLOAChaser,
    ],
  );

  // Launch campaign by creating a new email thread first (for faxed LORs)
  const handleLaunchWithNewThread = useCallback(
    async (
      recipientEmail: string,
      subject: string,
      body: string,
      phoneNumber?: string,
      adjusterPhone?: string,
      flowId?: string,
      startingStepIndex?: number,
      skipInitialDelay?: boolean,
      lorDocumentIds?: string[],
      emailStepContent?: Record<string, { body: string }>,
    ) => {
      setLoading(true);
      try {
        // AgentFlow mode - pass recipient_email to create new thread
        if (launchMode === 'agentflow') {
          if (!taskId) {
            toast.error('No acknowledgement task found for this coverage');
            return;
          }
          // Convert body-only content to API format (subject will be derived from thread)
          const apiEmailContent = emailStepContent
            ? Object.fromEntries(
                Object.entries(emailStepContent).map(([stepId, { body: stepBody }]) => [
                  stepId,
                  { subject: '', body: stepBody },
                ]),
              )
            : undefined;
          await startLOAChaser({
            matterId,
            coverageId,
            request: {
              task_id: taskId,
              recipient_email: recipientEmail,
              phone_number: phoneNumber,
              adjuster_phone: adjusterPhone,
              flow_id: flowId,
              starting_step_index: startingStepIndex,
              skip_initial_delay: skipInitialDelay,
              lor_document_ids: lorDocumentIds,
              email_step_content: apiEmailContent,
            },
          }).unwrap();
          toast.success('LOA chaser started');
          return;
        }

        // LOA Outreach mode - use the legacy loa-outreach API
        const launchUrl = `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${apiBasePath}/launch-with-new-thread/`;

        const res = await fetch(launchUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': Cookies.get('csrftoken') || '',
          },
          credentials: 'include',
          body: JSON.stringify({
            matter_id: matterId,
            coverage_id: coverageId,
            recipient_email: recipientEmail,
            subject,
            body,
            lor_document_ids: lorDocumentIds,
          }),
        });

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || `Launch failed (${res.status})`);
        }

        const data = await res.json();
        toast.success('Email sent and auto-chase enabled successfully');

        // Fetch the full campaign details
        const detailRes = await fetch(
          `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${getCampaignDetailUrl(apiBasePath, data.campaign_id, useSandbox)}`,
          { credentials: 'include' },
        );
        if (detailRes.ok) {
          const detailData = await detailRes.json();
          setCampaign(detailData);
          onCampaignLaunched?.(detailData.campaign);
          // Fetch activities for the new campaign
          await fetchActivities(data.campaign_id);
        }
      } catch (e: unknown) {
        const message =
          e instanceof Error
            ? e.message
            : 'Failed to send email and enable auto-chase';
        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [
      matterId,
      coverageId,
      onCampaignLaunched,
      apiBasePath,
      useSandbox,
      fetchActivities,
      launchMode,
    ],
  );

  // Send signal to campaign
  const handleSendSignal = useCallback(
    async (signal: string, params: Record<string, string | number>) => {
      if (!campaign) return;

      setSignalLoading(true);
      try {
        const res = await fetch(
          `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${getCampaignSignalUrl(apiBasePath, campaign.campaign.id, useSandbox)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': Cookies.get('csrftoken') || '',
            },
            credentials: 'include',
            body: JSON.stringify({ signal, params }),
          },
        );

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || `Signal failed (${res.status})`);
        }

        const data = await res.json();
        toast.success(data.message || 'Signal sent');

        // Update local state if response includes campaign data
        if (data.campaign) {
          setCampaign((prev) =>
            prev
              ? {
                  ...prev,
                  campaign: { ...prev.campaign, ...data.campaign },
                }
              : null,
          );
        }

        // For execution signals (skip_timer, set_step with execute=1, or manual_activity), poll for step change
        const isExecutionSignal =
          signal === 'skip_timer' ||
          signal === 'manual_activity' ||
          (signal === 'set_step' && Number(params.execute) === 1);

        // For set_step without execute (just repositioning), poll for the target step
        const isSetStepSignal =
          signal === 'set_step' && Number(params.execute) !== 1;

        // For pause/resume signals, poll for status change
        const isStatusChangeSignal =
          signal === 'loa_detected' || signal === 'resume';

        const campaignId = campaign.campaign.id;

        if (isExecutionSignal) {
          const previousStepIndex = campaign.campaign.current_step_index;

          // Poll for step change (step index should change after execution)
          const pollForChange = (attemptsLeft: number): Promise<void> => {
            return new Promise((resolve) => {
              setTimeout(async () => {
                if (attemptsLeft <= 0) {
                  resolve();
                  return;
                }
                try {
                  const pollRes = await fetch(
                    `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${getCampaignDetailUrl(apiBasePath, campaignId, useSandbox)}`,
                    { credentials: 'include' },
                  );
                  if (pollRes.ok) {
                    const pollData = await pollRes.json();
                    if (
                      pollData.campaign.current_step_index !== previousStepIndex
                    ) {
                      setCampaign(pollData);
                      onCampaignUpdated?.(pollData);
                      resolve();
                      return;
                    }
                  }
                } catch {
                  // Continue polling
                }
                pollForChange(attemptsLeft - 1).then(resolve);
              }, 1000);
            });
          };

          await pollForChange(6);
        }

        if (isSetStepSignal) {
          // Poll for step change when setting to a different step
          // Capture the previous step index before polling starts
          const previousStepIndex = campaign.campaign.current_step_index;

          const pollForSetStep = (attemptsLeft: number): Promise<void> => {
            return new Promise((resolve) => {
              setTimeout(async () => {
                if (attemptsLeft <= 0) {
                  resolve();
                  return;
                }
                try {
                  const pollRes = await fetch(
                    `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${getCampaignDetailUrl(apiBasePath, campaignId, useSandbox)}`,
                    { credentials: 'include' },
                  );
                  if (pollRes.ok) {
                    const pollData = await pollRes.json();
                    // Check if step index changed from previous value
                    if (
                      pollData.campaign.current_step_index !== previousStepIndex
                    ) {
                      setCampaign(pollData);
                      onCampaignUpdated?.(pollData);
                      resolve();
                      return;
                    }
                  }
                } catch {
                  // Continue polling
                }
                pollForSetStep(attemptsLeft - 1).then(resolve);
              }, 500);
            });
          };

          await pollForSetStep(10);
        }

        if (isStatusChangeSignal) {
          const previousStatus = campaign.campaign.status;

          // Poll for status change
          const pollForStatusChange = (attemptsLeft: number): Promise<void> => {
            return new Promise((resolve) => {
              setTimeout(async () => {
                if (attemptsLeft <= 0) {
                  resolve();
                  return;
                }
                try {
                  const pollRes = await fetch(
                    `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${getCampaignDetailUrl(apiBasePath, campaignId, useSandbox)}`,
                    { credentials: 'include' },
                  );
                  if (pollRes.ok) {
                    const pollData = await pollRes.json();
                    if (pollData.campaign.status !== previousStatus) {
                      setCampaign(pollData);
                      onCampaignUpdated?.(pollData);
                      resolve();
                      return;
                    }
                  }
                } catch {
                  // Continue polling
                }
                pollForStatusChange(attemptsLeft - 1).then(resolve);
              }, 500);
            });
          };

          await pollForStatusChange(10);
        }

        // Refresh campaign details
        const detailRes = await fetch(
          `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${getCampaignDetailUrl(apiBasePath, campaign.campaign.id, useSandbox)}`,
          { credentials: 'include' },
        );
        if (detailRes.ok) {
          const detailData = await detailRes.json();
          setCampaign(detailData);
          onCampaignUpdated?.(detailData);
        }
        // Refresh activities after signal
        await fetchActivities(campaign.campaign.id);
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : 'Failed to send signal';
        toast.error(message);
      } finally {
        setSignalLoading(false);
      }
    },
    [campaign, onCampaignUpdated, apiBasePath, useSandbox, fetchActivities],
  );

  // Handle email sent - refresh campaign details
  const handleEmailSent = useCallback(async () => {
    if (!campaign) return;

    // Poll for campaign update after email sent
    const pollForUpdate = async (attemptsLeft: number): Promise<void> => {
      if (attemptsLeft <= 0) return;

      try {
        const pollRes = await fetch(
          `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${getCampaignDetailUrl(apiBasePath, campaign.campaign.id, useSandbox)}`,
          { credentials: 'include' },
        );
        if (pollRes.ok) {
          const pollData = await pollRes.json();
          setCampaign(pollData);
          onCampaignUpdated?.(pollData);
          // Refresh activities
          await fetchActivities(campaign.campaign.id);
        }
      } catch {
        // Retry
      }

      // Schedule next poll
      setTimeout(() => {
        pollForUpdate(attemptsLeft - 1);
      }, 1000);
    };

    pollForUpdate(5);
  }, [campaign, apiBasePath, useSandbox, onCampaignUpdated, fetchActivities]);

  // Loading state
  if (isInitializing) {
    return (
      <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
        <div className="flex items-center justify-between px-3 py-1.5 bg-zinc-900">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-white">LOA Chaser</span>
          </div>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
        </div>
      </div>
    );
  }

  // No campaign - show launcher
  if (!campaign) {
    return (
      <Launcher
        matterId={matterId}
        coverageId={coverageId}
        claimNumber={claimNumber}
        onLaunch={handleLaunch}
        onLaunchWithNewThread={handleLaunchWithNewThread}
        loading={loading}
        channelAddress={channelAddress}
        hasFrontChannel={hasFrontChannel}
        isFrontChannelLoading={isFrontChannelLoading}
        firmId={firmId}
        suggestedThread={suggestedThread}
        suggestedThreadLoading={suggestedThreadLoading}
        defaultRecipientEmail={defaultRecipientEmail}
        defaultPhoneNumber={defaultPhoneNumber}
        defaultAdjusterPhone={defaultAdjusterPhone}
        showPhoneInput={launchMode === 'agentflow'}
        showStrategySelector={launchMode === 'agentflow'}
        launcherOpen={launcherOpen}
        onLauncherOpenChange={onLauncherOpenChange}
        hideLauncherButton={hideLauncherButton}
      />
    );
  }

  // Has campaign - show progress
  return (
    <div className="space-y-4">
      <LOAChaserProgress
        campaign={campaign.campaign}
        activities={activities}
        slaSteps={slaSteps}
        onSendSignal={handleSendSignal}
        onOpenEmail={
          campaign.campaign.email_thread_id
            ? () => {
                window.open(
                  `https://app.frontapp.com/open/${campaign.campaign.email_thread_id}`,
                  '_blank',
                );
              }
            : undefined
        }
        loading={signalLoading}
        carrierName={carrierName}
        matterId={matterId}
        onEmailSent={handleEmailSent}
      />

      {/* Task Activity Feed */}
      {taskId && (
        <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200">
            <span className="text-xs font-semibold text-zinc-700">
              Task Updates
            </span>
          </div>
          <div className="px-3 py-2">
            <TaskActivityFeed
              matterId={matterId}
              taskId={taskId}
              limit={10}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LOAChaserWidget;
